import { Link, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { LogOut } from "lucide-react";

import logo from "../assets/logo.png";

import PortalLogo from "./PortalLogo";

import useRBAC from "../hooks/useRBAC";
import usePortalFeatures from "../hooks/usePortalFeatures";
import { notifyAuthContextChanged } from "../utils/authCache";
import { usePerformanceTracker } from "./PerformanceTracker";
import { clearPresenceOnLogout } from "./PerformanceTracker/performanceTrackerApi";

import "./Sidebar.css";

function NavSectionDivider() {
  return (
    <div
      style={{
        height: "1px",
        background: "rgba(255,255,255,0.08)",
        margin: "14px 10px",
      }}
    />
  );
}

function NavSectionHeader({ label }) {
  return (
    <div
      style={{
        padding: "0 14px",
        fontSize: "11px",
        color: "#94a3b8",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        marginBottom: "8px",
      }}
    >
      {label}
    </div>
  );
}

export default function Sidebar({ onNavigate }) {
  const location = useLocation();
  const { accounts, instance } = useMsal();

  const handleNavClick = () => {
    onNavigate?.();
  };

  const { stopForLogout } = usePerformanceTracker();

  const handleSignOut = async () => {
    try {
      await clearPresenceOnLogout(instance, accounts);
    } catch {
      /* logout still proceeds */
    }
    try {
      await stopForLogout();
    } catch {
      /* logout still proceeds */
    }
    notifyAuthContextChanged();
    const currentUser = accounts?.[0];
    try {
      await instance.logoutRedirect({ account: currentUser });
    } catch {
      try {
        await instance.logoutPopup({ account: currentUser });
        window.location.href = "/";
      } catch (error) {
        console.error("Logout failed:", error);
      }
    }
  };

  const { isAdmin, loading: rbacLoading } = useRBAC();
  const { navigation, loading: featuresLoading } = usePortalFeatures();

  const loading = rbacLoading || featuresLoading;

  const renderNavLink = (item) => (
    <Link
      key={item.resource_path}
      to={item.resource_path}
      onClick={handleNavClick}
      className={`nav-item ${location.pathname === item.resource_path ? "active" : ""
        }`}
    >
      {item.navLabel}
    </Link>
  );

  if (loading) {
    return (
      <div className="sidebar">
        <div className="sidebar-header">
          <PortalLogo variant="sidebar" />
        </div>

        <div
          style={{
            padding: "20px",
            color: "#94a3b8",
          }}
        >
          Loading menu...
        </div>

        <div className="sidebar-footer">
          <button
            type="button"
            className="sidebar-signout"
            onClick={handleSignOut}
          >
            <LogOut size={16} />
            Sign Out
          </button>

          <div className="sidebar1-footer-logo">
            <img src={logo} alt="CFP Logo" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <PortalLogo variant="sidebar" />
      </div>

      <div className="sidebar-nav">
        <Link
          to="/"
          onClick={handleNavClick}
          className={`nav-item ${location.pathname === "/" ? "active" : ""}`}
        >
          Dashboard
        </Link>

        {navigation.topLevel.map(renderNavLink)}

        {navigation.groups.map((group) => (
          <div key={group.groupLabel} className="nav-group">
            <NavSectionDivider />
            <NavSectionHeader label={group.groupLabel} />
            {group.items.map(renderNavLink)}
          </div>
        ))}

        {isAdmin && (
          <div className="nav-group">
            <NavSectionDivider />
            <NavSectionHeader label="Administration" />

            <Link
              to="/admin-dashboard"
              onClick={handleNavClick}
              className={`nav-item ${location.pathname === "/admin-dashboard" ? "active" : ""
                }`}
            >
              Admin Console
            </Link>

            <Link
              to="/document-hub-v2"
              onClick={handleNavClick}
              className={`nav-item ${location.pathname === "/document-hub-v2" ? "active" : ""
                }`}
            >
              Work Pool
            </Link>

            <Link
              to="/manage"
              onClick={handleNavClick}
              className={`nav-item ${location.pathname === "/manage" ? "active" : ""
                }`}
            >
              Manage
            </Link>
            <Link
              to="/departments"
              onClick={handleNavClick}
              className={`nav-item ${location.pathname === "/departments" ? "active" : ""
                }`}
            >
              Departments
            </Link>
            <Link
              to="/miscellaneous"
              onClick={handleNavClick}
              className={`nav-item ${location.pathname === "/miscellaneous" ? "active" : ""
                }`}
            >
              Miscellaneous
            </Link>
            {/*
            <Link
              to="/performance-activity"
              onClick={handleNavClick}
              className={`nav-item ${location.pathname === "/performance-activity" ? "active" : ""}`}
            >
              Performance Activity
            </Link>
            */}
          </div>
        )}
      </div>

      <div className="sidebar-footer">
        <button
          type="button"
          className="sidebar-signout"
          onClick={handleSignOut}
        >
          <LogOut size={16} />
          Sign Out
        </button>

        <div className="sidebar1-footer-logo">
          <img src={logo} alt="CFP Logo" />
        </div>
      </div>
    </div>
  );
}
